<template>
  <div
    class="results-container"
  >
    <div v-if="isShowReport">
      <!-- header -->
      <div
        class="result-header"
      >
        <span
          @click="hideReport"
          class="absolute cursor-pointer left-4 hover:text-primary-100"
        >
          <aha-icon name="arrow-left" />
        </span>
        <p class="text-sm font-bold text-secondary-100">
          Presentation Report
        </p>
      </div>
      <!-- panel -->
      <ReportPanel />
      <!-- end panel -->
    </div>

    <aha-accordion v-else>
      <aha-accordion-item :default-expanded="true">
        <template slot="title">
          <h5 class="flex items-center">
            Report
          </h5>
        </template>
        <template slot="content">
          <!-- Report -->
          <p class="mb-3 text-xs text-base-90">
            <span v-if="presentation.exportData && presentation.exportData.reportUrl">
              Here is the engagement rate of this presentation.
              Click the button to see the full report.
            </span>
          </p>

          <div
            v-if="isLoadingReport ||
              (!isLoadingReport
                && presentation.exportData
                && presentation.exportData.reportUrl
                && presentationReport)"
          >
            <ReportEngagementRate
              v-if="!isLoadingReport"
              :engagementRate="engagementRate"
              :engagementLevel="engagementLevel"
              :showTooltip="true"
              :showIcon="true"
              class="mb-2"
            />
            <div
              v-else
              class="flex flex-col mb-2"
            >
              <report-engagement-rate-skeleton />
            </div>
            <aha-button
              variant="base"
              @click="viewReport()"
            >
              <aha-icon
                name="multiple-choice-w"
                width="18"
                height="18"
                viewBox="0 0 30 30"
                class="mr-1"
              />
              View more
            </aha-button>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item>
        <template slot="title">
          <h5>
            Export to Excel
          </h5>
        </template>
        <template slot="content">
          <p class="mb-3 text-xs">
            Export questions and audience responses to an Excel file.
          </p>

          <div v-if="$store.state.currentUser.hasExport">
            <aha-button
              variant="base"
              :disabled="loadingExportExcel || exportExcelInProgress"
              @click="exportExcel()"
            >
              <aha-spinner v-if="loadingExportExcel || exportExcelInProgress" />
              <font-awesome-icon
                :icon="['far', 'file-excel']"
                v-else
              />

              <span v-if="!hasExcelFile">Request Excel file...</span>
              <span v-else-if="loadingExportExcel || exportExcelInProgress">
                Generating Excel file...
              </span>
              <span v-else>Request updated file...</span>
            </aha-button>
            <p v-if="hasExcelExportError" class="mt-3 text-xs text-red-100">
              Something went wrong with the export process. Please try again.
            </p>
          </div>
          <div class="mb-3" v-else>
            <p class="mb-2 text-xs intro">
              Please upgrade your account to enable this feature.
            </p>
            <aha-button variant="success" @click="openUpgrade()">
              Upgrade now
            </aha-button>
          </div>

          <div class="mt-3" v-if="hasExcelFile && !isGuestUser">
            <p class="mb-2 text-xs">
              Last generated:
              {{ $store.state.presentation.exportData.excelSuccessAt | dateTimeAgo(false) }}
            </p>
            <ul class="pl-4 list-disc">
              <li>
                <a
                  class="mr-2 font-bold text-link--primary"
                  :href="$store.state.presentation.exportData.excelUrl"
                  target="_blank"
                >Download Excel file</a>
              </li>
            </ul>
          </div>
        </template>
      </aha-accordion-item>

      <aha-accordion-item>
        <template slot="title">
          <h5 class="flex items-center">
            Export to PDF / JPG
          </h5>
        </template>

        <template slot="content">
          <p class="mb-3 text-xs">
            Export screenshots of your slides (with audience responses)
            into a PDF document or as a set of JPG images. This may take a few minutes to complete.
          </p>

          <div v-if="$store.state.currentUser.hasExport">
            <aha-button
              variant="base"
              :disabled="loadingExportPdf || exportPdfInProgress"
              @click="exportPdf()"
            >
              <aha-spinner v-if="loadingExportPdf || exportPdfInProgress" />
              <font-awesome-icon :icon="['far', 'file-pdf']" v-else />

              <span v-if="!hasPdfFile">Request screenshots...</span>
              <span v-else-if="exportPdfInProgress">Generating screenshots...</span>
              <span v-else>Request new screenshots...</span>
            </aha-button>
            <p v-if="hasPdfExportError" class="mt-3 text-xs text-red-100">
              Something went wrong with the export process. Please try again.
            </p>
            <div class="mt-3" v-if="hasPdfFile && !isGuestUser">
              <p class="mb-3 text-xs">
                Last generated: {{ $store.state.presentation.exportData.successAt | dateTimeAgo(false) }}
              </p>
              <ul
                class="pl-4 font-bold list-disc"
              >
                <li>
                  <a
                    class="cursor-pointer text-link--primary"
                    :href="$store.state.presentation.exportData.pdfUrl"
                    target="_blank"
                  >Download PDF file</a>
                </li>
                <li>
                  <a
                    class="cursor-pointer text-link--primary"
                    :href="$store.state.presentation.exportData.zipUrl"
                    target="_blank"
                  >Download JPG files (as ZIP)</a>
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-3" v-else>
            <p class="mb-3 text-xs intro">
              Please upgrade your account to enable this feature.
            </p>
            <aha-button variant="success" @click="openUpgrade()">
              Upgrade now
            </aha-button>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item>
        <template slot="title">
          <h5>Reset results</h5>
        </template>
        <template slot="content">
          <!-- Clear audience response -->
          <p class="mb-3 text-xs">
            Delete all <u
              v-tooltip="resetResultsTooltip"
              class="cursor-pointer"
            >results</u> from participants in this presentation.
          </p>
          <ResetResultsButton
            v-if="presentation.id"
            gaAction="Result menu"
            class="block mb-3"
            hideTooltip
          />
          <p class="text-xs">
            To keep the current results,
            you can make a clean duplicate of this presentation.
          </p>
          <aha-button
            variant="link"
            class="justify-start pl-0 mb-3 text-xs underline text-base-80"
            @click="duplicatePresentation()"
            :disabled="isGuestUser"
          >
            <span
              v-tooltip="{content:isGuestUser?'This feature is disabled in Demo Mode':false}"
            >Duplicate this presentation</span>
            <aha-spinner v-if="loadingDuplicate" />
          </aha-button>
        </template>
      </aha-accordion-item>
    </aha-accordion>

    <!-- <aha-modal
      v-if="errorOnErasingPresentationData !== null"
      title="Error on erasing data"
      @hide="errorOnErasingPresentationData = null"
    >
      <p class="mb-2 text-red-100" v-html="errorOnErasingPresentationData" />
      <p class="mb-4">
        Please try again
      </p>
      <aha-button @click="errorOnErasingPresentationData = null" class="w-20">
        OK
      </aha-button>
    </aha-modal> -->
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import Config from '@/config'
import Utils from '@/util'
import constants from '@/constant'
import EventBus from '@/eventBus'

import ReportEngagementRate from '@/components-v2/presentation-report/ReportEngagementRate.vue'
import ReportEngagementRateSkeleton from '@/components-v2/presentation-report/ReportEngagementRateSkeleton.vue'
import ReportPanel from '@/components-v2/presentation-report/ReportPanel.vue'
import ResetResultsButton from '@/components-v2/common/reset-results/ResetResultsButton.vue'
import mixpanelUtils from '@/utils/mixpanel.utils'

const { gaCategories } = constants

export default {
  components: {
    ReportEngagementRate,
    ReportEngagementRateSkeleton,
    ReportPanel,
    ResetResultsButton,
  },
  props: {
    contentVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      linkDownload: '',
      loading: false,
      loadingClearData: false,
      loadingDuplicate: false,
      loadingExportPdf: false,
      loadingExportExcel: false,
      isShowReport: false,
    }
  },
  computed: {
    ...mapGetters('presentationReport', [
      'engagementRate',
      'presentationReport',
      'engagementLevel',
      'isLoadingReport',
    ]),
    ...mapState({
      isGuestUser: (state) => state.currentUser.isGuest,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    exportPdfInProgress() {
      return this.$store.state.presentation.exportData
       && this.$store.state.presentation.exportData.status === 'processing'
       && this.$store.state.presentation.exportData.requestAt > Date.now() - 30 * 60000
    },
    hasPdfFile() {
      return this.$store.state.presentation.exportData &&
       !!this.$store.state.presentation.exportData.pdfUrl
    },
    hasPdfExportError() {
      return this.$store.state.presentation.exportData && (
        this.$store.state.presentation.exportData.status === 'failure' ||
        (this.$store.state.presentation.exportData.status === 'processing' &&
         this.$store.state.presentation.exportData.requestAt < Date.now() - 30 * 60000)
      )
    },
    exportExcelInProgress() {
      return this.$store.state.presentation.exportData
       && this.$store.state.presentation.exportData.excelStatus === 'processing'
       && this.$store.state.presentation.exportData.excelRequestAt > Date.now() - 30 * 60000
    },
    hasExcelFile() {
      return this.$store.state.presentation.exportData &&
       !!this.$store.state.presentation.exportData.excelUrl
    },
    hasExcelExportError() {
      return this.$store.state.presentation.exportData && (
        this.$store.state.presentation.exportData.excelStatus === 'failure' ||
        (this.$store.state.presentation.exportData.excelStatus === 'processing' &&
         this.$store.state.presentation.exportData.excelRequestAt < Date.now() - 5 * 60000)
      )
    },
    resetResultsTooltip() {
      return '<p>Results include all participants’ <b> votes</b>, <b>submissions</b> and <b>reactions</b>.</p>'
    },
  },
  mounted() {
    this.selectPresentationAction({
      presentation: this.presentation,
    })
  },
  destroyed() {
    this.clearPresentationReportAction()
    Utils.gaTrackEvent(constants.gaCategories.PRESENTATION_EDITOR, 'Results menu > Report > Close results menu & hide report')
  },
  methods: {
    ...mapActions('presentationReport', [
      'selectPresentationAction',
      'openReportPanelAction',
      'clearPresentationReportAction',
      'generateReportAction',
      'subscribeReportAction',
    ]),
    exportPdf() {
      if (this.loadingExportPdf) return
      this.loadingExportPdf = true

      this.$http.post(Config.workerApi.exportPdf, {
        id: this.$store.state.presentation.id,
      }).then(() => {
        const exportData = { ...this.$store.state.presentation.exportData, status: 'processing', requestAt: Date.now() }
        this.$store.dispatch('updatePresentation', { exportData })
        this.loadingExportPdf = false
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Request Export PDF > Success')
      },
      (err) => {
        const exportData = { ...this.$store.state.presentation.exportData, status: 'failure', failureAt: Date.now() }
        this.$store.dispatch('updatePresentation', { exportData })
        this.loadingExportPdf = false
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Request Export PDF > Failed', JSON.stringify(err))
      })
    },

    async duplicatePresentation() {
      if (this.loadingDuplicate) return
      this.loadingDuplicate = true
      const isOwner = this.presentation.userId === this.currentUser.id
      let newPresentationId

      if (isOwner) {
        const duplicateResponse = await this.$http.post(Config.api.duplicatePresentation, {
          name: this.presentation.name,
          id: this.presentation.id,
        })
        newPresentationId = duplicateResponse.body.id
      } else {
        const copyResponse = await this.$http.post(Config.api.copyPresentation, {
          id: this.presentation.shareCode,
          name: this.presentation.name,
          fromWorkspace: !!((this.currentUser.accountId && this.presentation.accountId)),
        })
        newPresentationId = copyResponse.body.id
      }

      this.loadingDuplicate = true
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Create new presentation', 'Results menu > Duplicate presentation', this.presentation.id, {
        sourcePresentationId: this.presentation.id,
        ...mixpanelUtils.getPresentationContext(this.presentation),
      })
      window.location.href = `${window.location.origin}/presentation/${newPresentationId}`
    },
    async clearData() {
      if (this.loadingClearData) return

      this.loadingClearData = true

      const {
        id: presentationId, uniqueAccessCode, teamPlay,
      }
        = this.presentation

      const payload = {
        id: presentationId,
      }
      if (teamPlay) {
        payload.uniqueAccessCode = uniqueAccessCode
        payload.teamPlay = teamPlay
      }

      try {
        await this.$http.post(Config.api.clearAudienceData, payload)
      } catch (error) {
        const errorMsg = JSON.stringify(error)
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Clear Audience data > Failed', errorMsg)

        throw error
      } finally {
        this.loadingClearData = false
      }

      this.clearPresentationReportAction()
      this.$store.dispatch('updatePresentation', {
        exportData: {
          ...this.presentation,
          reportUrl: null,
          reportStatus: null,
          reportUpdatedAt: null,
        },
      })

      this.$store.state.presentation.slides.forEach((slide) => {
        if (slide.type === 'openEnded' || slide.type === 'wordCloud' || slide.type === 'ideas') {
          slide.SlideOptions = []
        }
        if (slide.type === 'spinnerWheel') {
          EventBus.$emit('clearSpinnerWheel', slide.id)
          slide.metadata = {
            ...slide.metadata,
            winner: null,
            entries: null,
            entryUrl: null,
            wheelStatus: null,
          }
        }

        if (slide.type === 'multipleChoice' || slide.type === 'pickAnswer' || slide.type === 'imageChoice') {
          slide.SlideOptions.forEach((slideOption) => {
            slideOption.count = 0
          })
        }
        slide.multipleCountAnswer = 0
        slide.votingStep = constants.IdeasTabName.SUBMISSION
      })

      this.$store.state.audiences = {}
      this.$store.state.countPlayer = 0

      const index = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === this.$store.state.slideActive.id
      })

      this.$store.dispatch('updateSlideActive', { id: null, activeTab: null })
      setTimeout(() => {
        this.$store.dispatch(
          'setSlideActive',
          JSON.parse(JSON.stringify(this.$store.state.presentation.slides[index])),
        )
      }, 0)
      this.$store.state.questions = []
      this.$store.state.audiences = {}
      this.$store.state.tabQnaQuestion = 1
      this.$store.state.lastQuestion = {}
      this.$store.state.questionCount = 0
      this.$store.state.presentation.showExport = false
      this.$store.state.countTopQuestion = 0
      this.$store.state.countPinnedQuestion = 0
      this.$store.state.countAnswerQuestion = 0
      this.$store.state.presentation.numberOfHearts = 0
      this.$store.state.presentation.numberOfLaughs = 0
      this.$store.state.presentation.numberOfLikes = 0
      this.$store.state.presentation.numberOfSads = 0
      this.$store.state.presentation.numberOfWows = 0

      Utils.showToastSuccess('Presentation data erased.')
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Clear Audience data > Success')

      if (this.$store.state.presentation.teamData) {
        this.$store.state.presentation.teamData.forEach((team) => {
          team.teams = []
        })
      }
    },

    hideSetting() {
      this.$store.state.presentation.showExport = false
      document.body.classList.remove('no-scroll')
    },

    exportExcel() {
      if (this.loadingExportPdf) return
      this.loadingExportExcel = true

      this.$http.post(Config.workerApi.exportExcel, {
        id: this.$store.state.presentation.id,
      }).then(() => {
        const exportData = {
          ...this.$store.state.presentation.exportData,
          excelStatus: 'processing',
          excelRequestAt: Date.now(),
        }
        this.$store.dispatch('updatePresentation', { exportData })
        this.loadingExportExcel = false
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Export Excel > Success')
      },
      (err) => {
        const exportData = { ...this.$store.state.presentation.exportData, excelStatus: 'failure' }
        this.$store.dispatch('updatePresentation', { exportData })
        this.loadingExportExcel = false
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Export Excel > Failed', `Request export excel fail ${JSON.stringify(err)}`)
      })
    },
    openUpgrade() {
      // add redirect to handle case: guest clicks upgrade -> go to sign-up first
      this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },
    generateReport() {
      this.generateReportAction(this.presentation)
      this.subscribeReportAction(this.presentation)

      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'View report', this.presentation.id)
    },
    viewReport() {
      this.isShowReport = !this.isShowReport
      this.$emit('handle-hide-main-header')
      this.openReportPanelAction()
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Results menu > Report > Click view more')
    },
    hideReport() {
      this.isShowReport = !this.isShowReport
      this.$emit('handle-hide-main-header')
      Utils.gaTrackEvent(constants.gaCategories.PRESENTATION_EDITOR, 'Results menu > Report > Hide report')
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-result {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
}

.result-header {
  @apply sticky top-0 z-20 flex items-center justify-center h-16;
  @apply p-4 shadow-top bg-base-0 border-base-30 text-base-90;
}
</style>
